/*
 *
 * Copyright 2022, White River Computing LLC. All rights reserved.
 * For licensing information, please contact Charlie Flock (cflock@whiterivercomputing.com or 888-802-6340)
 */
import './WrcVisitReports.css';

import React from "react";
import PropTypes from "prop-types";

import {Card, Elevation, HTMLTable} from "@blueprintjs/core";
import {Icon, Spinner, NonIdealState} from "@blueprintjs/core";
import {Tabs, Tab} from "@blueprintjs/core";
import * as IconNames from "@blueprintjs/icons/lib/esm/generated/iconNames";

import {Chart, Pies, Transform} from 'rumble-charts';

import {WrcCardHeader} from "./WrcCardHeader";
import {WrcServer} from "./WrcServer";
import {WrcColors} from "./WrcBranding";

export class VisitReportTab extends React.PureComponent {

    toggleDrilldown(e, data) {
        e.preventDefault();
        if (this.props.getDrilldownStatus() && data.drilldown_value === this.props.getDrilldownValue()) {
            this.props.toggleDrilldownStatus()
        } else {
            this.props.visitDrilldown(data)
        }
    }

    getClickableRow = (title, short_title) => {
        if (this.props.content.drilldown_groupby) {
            return (<a href="#" onClick={(e) => this.toggleDrilldown(e, {...this.props.content, 'drilldown_value': title, 
            'visit_column': this.props.visitColumn, 'visit_schema': this.props.visitSchema, 'report_tables': this.props.visitTables})}>{short_title}</a>)
        }
        return short_title
    }

    parseTableData(title, accdata, total, label) {
        let tablerows = [], keyname = title + '-' + label;
        for (let i = 0; i < accdata.length; i++) {
            let acc_tabrow = accdata[i];
            if (acc_tabrow.y || 
                (this.props.content.valid_cohorts && this.props.content.valid_cohorts.includes(acc_tabrow.title))) {
                tablerows.push(
                    <tr key={'acc-tabrow-' + keyname + '-' + i}>
                        <td style={{whiteSpace: 'nowrap'}}><Icon icon={IconNames.SYMBOL_SQUARE}
                                color={acc_tabrow.color}
                                style={{marginRight: '.2em'}}/>
                            {this.getClickableRow(acc_tabrow.title, acc_tabrow.short_title)}
                        </td>
                        <td>{acc_tabrow.baseline}</td>
                        <td>{acc_tabrow.follow_up}</td>
                        <td>{acc_tabrow.off_schedule}</td>
                    </tr>
                );
            }
        }

        tablerows.push(
            <tr key={'acc-tabrow-totals-' + keyname}>
                <td>Total</td>
                <td>{this.props.total_baseline_visits}</td>
                <td>{this.props.total_follow_up_visits}</td>
                <td>{this.props.total_off_schedule_visits}</td>
            </tr>
        );

        return tablerows;
    }

    constructor(props) {
        super(props);
        this.parseTableData = this.parseTableData.bind(this)
        this.toggleDrilldown = this.toggleDrilldown.bind(this)
        const thedata = props.content,
            tot_all = thedata.total;

        let alldata = [], thecolor = 0;
        if (thedata.responses) {
            for (let [key, value] of Object.entries(thedata.responses)) {
                const color = WrcColors.CHART_COLORS_L1[(thecolor++) % WrcColors.CHART_COLORS_L1.length];
                alldata.push({title: key, short_title: props.cohort_short_names[key] ? props.cohort_short_names[key] : key, y: value['total'], color: color, 
                              baseline: String(value['baseline']), follow_up: String(value['follow_up']), 
                              off_schedule: String(value['off_schedule'])});
            }
        }
        
        // may have to pass totals here
        const taball = this.parseTableData(props.content.title + '-all', alldata, tot_all, 'All Sites');
        this.state = {
            acc_data_all: [{ data: alldata }],
            cur_data:  [{ data: alldata }],
            table_data_all: taball,
            cur_table: taball,
            tot_all: tot_all
        };
    }

    render() {
        const thetitle =
            <span>
                {this.props.content.title + ' Visits'}
            </span>;

        let renderedChart=undefined;
        if(this.state.tot_all === 0){
            renderedChart=(
                <NonIdealState className='non-ideal-state' icon='pie-chart' title='No Visit Data Accrued' description='Please add visit data for the selected category to see visit report.'/>
            );
        } else {
            renderedChart= (
                <Chart width={140} height={140} series={this.state.cur_data}  className='pie-chart'>
                    <Transform method={['transpose', 'stack']}>
                        <Pies combined={true} />
                    </Transform>
                </Chart>
            );
        }
        return (
            <Card className='report-card' interactive={false}>
                <WrcCardHeader title={thetitle}/>
                <div className ='summary-chart-container'>
                    {renderedChart}
                </div>

                <div className='summary-table-container'>
                    <HTMLTable condensed={true} bordered={true}
                        key={'accrual-table-' + thetitle}
                        className='summary-table'>
                        <thead>
                        <tr>
                            <th>Cohort</th>
                            <th>Baseline</th>
                            <th>Follow-Up</th>
                            <th>Off-Schedule</th>
                        </tr>
                        </thead>
                        <tbody>
                        {this.state.cur_table}
                        </tbody>
                    </HTMLTable>
                </div>
            </Card>
        );
    }
}

VisitReportTab.propTypes = {
    showreportshandler: PropTypes.func.isRequired,
    content: PropTypes.object.isRequired,
};

export class WrcVisitReports extends React.PureComponent {
    parseVisitData(thecontent) {
        let reporttabs = [];
        for (let i = 0; i < thecontent.length; i++) {
            reporttabs.push(<Tab title={<span>{thecontent[i].title}</span>} id={'report-' + i + '-tab'} key={'report-' + i + '-tab'} panel={
                <VisitReportTab key={'accrualtab-' + i}
                                    content={thecontent[i]}
                                    visitDrilldown={this.visitDrilldown}
                                    getDrilldownValue={this.getDrilldownValue}
                                    toggleDrilldownStatus={this.toggleDrilldownStatus}
                                    getDrilldownStatus={this.getDrilldownStatus}
                                    total_baseline_visits={thecontent[i]['total_baseline_visits']}
                                    total_follow_up_visits={thecontent[i]['total_follow_up_visits']}
                                    total_off_schedule_visits={thecontent[i]['total_off_schedule_visits']}
                                    cohort_short_names={this.props.cohort_short_names}
                                    participant_table={this.props.participant_table}
                                    visitSchema={this.props.report_schema}
                                    visitColumn={this.props.report_column}
                                    visitTables={this.props.report_tables}
                                    />
            }/>);
        }
        this.setState({reportTabs: reporttabs});
    }

    constructor(props) {
        super(props);
        this.state = {reportTabs: []};
        this.visitDrilldown = this.visitDrilldown.bind(this)
    }

    componentDidMount() {
        this.parseVisitData(this.props.data)
    }

    capitalize(word) {
        return word[0].toUpperCase() + word.slice(1).toLowerCase();
      }

    toggleDrilldownStatus = () => {
        this.setState({cohort_selected: !this.state.cohort_selected})
    }

    getDrilldownStatus = () => {
        return this.state.cohort_selected
    }

    getDrilldownValue = () => {
        return this.state.drilldown_value
    }

    displayDrilldown() {
        let thecolor = 0;
        let table_data = this.state.drilldown_data.map((row) => {
            const icon_color = WrcColors.CHART_COLORS_L1[(thecolor++) % WrcColors.CHART_COLORS_L1.length];
            return (
                <tr>
                    <td>
                        <Icon icon={IconNames.SYMBOL_SQUARE}
                                    color={icon_color}
                                    style={{marginRight: '.2em'}}/>
                        {row.site}
                    </td>
                    <td>
                        {row.baseline}
                    </td>
                    <td>
                        {row.follow_up}
                    </td>
                    <td>
                        {row.off_schedule}
                    </td>
                </tr>
            )
        })
        return (
        <span className='summary-table-container'>
            <HTMLTable condensed={true} bordered={true}
                className='summary-table'>
                <thead>
                <tr>
                    <th>Site</th>
                    <th>Baseline</th>
                    <th>Follow-up</th>
                    <th>Off-Schedule</th>
                </tr>
                </thead>
                <tbody>
                {table_data}
                </tbody>
            </HTMLTable>
        </span>
        )
    }

    displayChart() {
        let alldata = [], thecolor = 0;

        if (this.state.drilldown_data) {
            for (let i=0; i<this.state.drilldown_data.length; i++) {
                const color = WrcColors.CHART_COLORS_L1[(thecolor++) % WrcColors.CHART_COLORS_L1.length];
                // cohort, site, count
                alldata.push({title: this.state.drilldown_data[i][[this.state.grouped_by]], y: parseInt(this.state.drilldown_data[i]['total']), color: color, x: i});
            }
        }
        let chart_data = [{data: alldata}]
        let renderedChart=undefined;
        if(this.state.drilldown_data.length === 0){
            renderedChart=(
                <span>
                    <NonIdealState className='non-ideal-state' icon='pie-chart' title='No Specimen Data Accrued' description='Please add specimen data for the selected category to see specimen report.'/>
                </span>
            );
        } else {
            renderedChart= (
                <span>
                    <Chart width={140} height={140} series={chart_data}  className='pie-chart'>
                        <Transform method={['transpose', 'stack']}>
                            <Pies combined={true} />
                        </Transform>
                    </Chart>
                </span>
            );
        }
        return renderedChart
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevState.cohort_selected !== this.state.cohort_selected) {
            if (this.state.cohort_selected) {
                document.getElementById('visit-drilldown').scrollIntoView(false)
            }
        }
    }

    visitDrilldown = (data) => {
        WrcServer.post('get-site-visits',
        data,
        (resp) => {
                let drilldown_data = Object.keys(resp).map((site) => {
                    let new_data = resp[site]
                    new_data['site'] = site
                    return new_data
                })
                this.setState({
                    cohort_selected: true,
                    drilldown_cohort: data.title,
                    grouped_by: data.drilldown_groupby,
                    drilldown_value: data.drilldown_value,
                    drilldown_data: drilldown_data
                })
            }
        )
    }

    render() {
        let card_alignment= this.props.component_alignment === 'center' ? 'center' : 'left';
        let report_full_class= 'report-div ' + card_alignment;
        let drilldown_component = this.state.cohort_selected ?  this.state.drilldown_data.length > 0 ? (                    
        <div >  
            <Card id="visit-drilldown" className="drilldown-card">
                <WrcCardHeader small={true} title={this.state.drilldown_cohort} />
                <WrcCardHeader small={true} title={this.state.drilldown_value} />
                <div className='rowC'>
                    {this.displayChart()}
                    {this.displayDrilldown()}
                </div>
            </Card>
        </div>) 
        : (<div><Card id="visit-drilldown" className="drilldown-card"><NonIdealState className='non-ideal-state' icon='pie-chart' title='No Visit Data for Site' description='Please add visit data for the selected cohort to see site report.'/></Card></div>) 
        : (<div id="visit-drilldown"></div>)
        if (!this.state.reportTabs.length) {
            return (
                <div className={report_full_class}>
                    <div>
                        <Spinner/>
                    </div>
                </div>
            );
        } else if (this.state.tot_site === 0) {
            return null;
        } else {
            return (
                    <div>
                        {this.state.reportTabs}
                        { drilldown_component }
                    </div>
            );
        }
    }
}

WrcVisitReports.propTypes = {
    reports: PropTypes.array.isRequired
};
